'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface MHDropdownItem {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface MHDropdownProps {
  trigger: React.ReactNode;
  items: MHDropdownItem[];
  align?: 'left' | 'right';
  className?: string;
}

export function MHDropdown({ trigger, items, align = 'right', className }: MHDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div className={cn('relative inline-block', className)} ref={wrapperRef}>
      <div
        role="button"
        tabIndex={0}
        className="inline-flex"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((value) => !value);
          }
        }}
      >
        {trigger}
      </div>

      {open ? (
        <div
          className={cn(
            'absolute z-40 mt-2 min-w-[180px] rounded-xl border border-border-subtle bg-surface-1 p-1 shadow-sm',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          {items.map((item) => (
            <button
              key={item.label}
              disabled={item.disabled}
              onClick={() => {
                item.onClick?.();
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center rounded-lg px-3 py-2 text-left text-[14px] font-medium text-text-2 transition-colors',
                'hover:bg-surface-2 hover:text-text-1',
                'disabled:cursor-not-allowed disabled:opacity-40'
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
